import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import apiClient from "@/api/client";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { ProjectEditForm } from "@/features/enterprises/components/ProjectEditForm";
import type { Project } from "@/features/enterprises/types";
import { usePageTitle } from "@/hooks/usePageTitle";

export function ProjectEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isCeo = Boolean(user?.is_ceo);
  const isTechnicalDirector = user?.role === "director" && Boolean(user?.is_technical_director);
  const isCoordinator = user?.role === "coordinator";

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ["projects", id],
    queryFn: () =>
      apiClient
        .get<{ data: Project[] }>("/projects/", { params: { project: id } })
        .then((r) => r.data.data[0] ?? null),
    enabled: !!id,
  });
  usePageTitle(project && `Edycja: ${project.name}`);

  if (projectLoading) return <PageLoader />;

  const backLink = (
    <Link
      to={`/projects/${id}`}
      className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
    >
      <ArrowLeft size={14} /> Przedsięwzięcie
    </Link>
  );

  // Coordinators only receive projects they are assigned to, so a missing project here
  // means it does not exist or the user has no access to it.
  if (!project) {
    return (
      <div className="max-w-2xl">
        {backLink}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Nie znaleziono przedsięwzięcia lub brak do niego dostępu.
        </p>
      </div>
    );
  }

  // Edit access: CEO, technical director, or a coordinator assigned to this project.
  const isAssignedCoordinator = isCoordinator && !!user && project.coordinators.includes(user.id);
  const canEdit = isCeo || isTechnicalDirector || isAssignedCoordinator;
  if (!canEdit) {
    return (
      <div className="max-w-2xl">
        {backLink}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Nie masz uprawnień do edycji tego przedsięwzięcia.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {backLink}

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edytuj przedsięwzięcie</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>

      <ProjectEditForm
        project={project}
        onSaved={() => navigate(`/projects/${id}`)}
        onCancel={() => navigate(`/projects/${id}`)}
      />
    </div>
  );
}
